import type { FC, ReactNode } from 'react';
import classNames from 'classnames';
import { Button, RadioTile, Tag } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import type { LandingIntervalCard } from './types';
import styles from './landing-interpretation-section.module.scss';

interface IntervalCardDeckProps {
  cards: LandingIntervalCard[];
  selectedId: number | null;
  /** The applied interpretation; its card turns green and the rest of the deck locks. */
  appliedId: number | null;
  readOnly: boolean;
  onSelect: (id: number) => void;
  /** The reference pane pinned on the left (the selected run's parameters). */
  lead?: ReactNode;
  catalogCard: {
    heading: string;
    description: string;
    buttonText: string;
    onOpen: () => void;
  };
}

const wells = (count: number | null) => {
  if (count === null) return '—';
  return `${formatNumber(count, 0, 0)} wells`;
};

// The interval deck (INS-12936), ported from the Target Zone tile row: a reference pane on the
// left, selectable cards beside it, and the catalog card parked last. Percent in Zone takes the
// slot the confidence score holds there: it is the number that argues for the card.
export const IntervalCardDeck: FC<IntervalCardDeckProps> = ({
  cards,
  selectedId,
  appliedId,
  readOnly,
  onSelect,
  lead,
  catalogCard,
}) => (
  <div className={styles.tiles}>
    {lead && <div className={styles.leadPane}>{lead}</div>}
    <div className={styles.tileRow}>
      {cards.map((card) => {
        const applied = appliedId === card.id;
        return (
          <RadioTile
            key={card.id}
            id={`landing-interval-card-${card.id}`}
            name="landing-interval-card"
            value={String(card.id)}
            checked={selectedId === card.id}
            // Once a reading is marked correct the deck locks, so a stray click cannot diverge
            // the card from what was applied. Remove Correct Status unlocks it.
            disabled={readOnly || (appliedId !== null && !applied)}
            className={classNames(styles.tile, {
              [styles.tileApplied]: applied,
            })}
            onChange={() => onSelect(card.id)}
          >
            <div className={styles.tileHeader}>
              <span className={styles.tileEyebrow}>
                {card.percentInZone === null ? 'Geologic Interval' : 'In Zone'}
              </span>
              {card.isCurrent && (
                <Tag type="cyan" size="sm">
                  Current
                </Tag>
              )}
            </div>
            <p className={styles.tileName}>{card.name}</p>
            <dl className={styles.tileFields}>
              <div className={styles.tileField}>
                <dt>Nearby Landed Wells in Interval</dt>
                <dd>{wells(card.nearbyLandedWells)}</dd>
              </div>
              <div className={styles.tileField}>
                <dt>Grid Surface</dt>
                <dd>{card.gridSurface ?? '—'}</dd>
              </div>
            </dl>
            {card.percentInZone !== null && (
              <div className={styles.tileScoreSection}>
                <hr className={styles.tileDivider} />
                <div className={styles.tileScore}>
                  <span className={styles.tileScoreLabel}>Percent in Zone</span>
                  <span className={styles.tileScoreValue}>
                    {`${formatNumber(card.percentInZone, 0, 1)}%`}
                  </span>
                </div>
              </div>
            )}
          </RadioTile>
        );
      })}
      <div className={classNames(styles.tile, styles.tilePrompt)}>
        <span className={styles.tileEyebrow}>Geologic Interval</span>
        <p className={styles.tilePromptHeading}>{catalogCard.heading}</p>
        <p className={styles.tilePromptDesc}>{catalogCard.description}</p>
        <div className={styles.tilePromptActions}>
          <Button
            kind="primary"
            size="sm"
            renderIcon={Edit}
            iconDescription={catalogCard.buttonText}
            disabled={readOnly || appliedId !== null}
            onClick={catalogCard.onOpen}
          >
            {catalogCard.buttonText}
          </Button>
        </div>
      </div>
    </div>
  </div>
);
